<template>
    <div class="ei-edit">
        <div v-if="loading" class="ei-edit-loading"><i class="fas fa-spinner fa-spin"></i> 로드 중…</div>
        <div v-else-if="error" class="ei-edit-error">{{ error }}</div>
        <template v-else>
            <section class="ei-edit-section">
                <div class="ei-edit-section-head">기본 설정</div>
                <div class="ei-edit-grid">
                    <div class="ei-edit-ro">
                        <span class="ei-edit-label">카테고리</span>
                        <span class="ei-edit-value">{{ master.enIntCategory }}</span>
                    </div>
                    <div class="ei-edit-ro">
                        <span class="ei-edit-label">단위</span>
                        <span class="ei-edit-value">{{ master.baseUnit }} → {{ master.intensityUnit }}</span>
                    </div>
                    <label class="ei-edit-field">
                        <span class="ei-edit-label">이름</span>
                        <input
                            v-model.trim="edit.enIntName"
                            class="ei-edit-input"
                            type="text"
                            maxlength="100"
                            placeholder="원단위 이름"
                        />
                    </label>
                    <label class="ei-edit-field">
                        <span class="ei-edit-label">자동 계산</span>
                        <select v-model="edit.calcEnabled" class="ei-edit-select">
                            <option value="Y">사용</option>
                            <option value="N">미사용</option>
                        </select>
                    </label>
                    <label class="ei-edit-field ei-edit-field--full">
                        <span class="ei-edit-label">기준값 입력 방식</span>
                        <select v-model="edit.dataInputType" class="ei-edit-select">
                            <option :value="ENINT_DATA_INPUT_NEW">신규 관제점(직접 생성)</option>
                            <option :value="ENINT_DATA_INPUT_EXISTING">기존 관제점(기준값 매핑)</option>
                        </select>
                    </label>
                    <label class="ei-edit-field ei-edit-field--full">
                        <span class="ei-edit-label">비고</span>
                        <textarea
                            v-model.trim="edit.rmk"
                            class="ei-edit-textarea"
                            rows="2"
                            placeholder="선택 입력"
                        ></textarea>
                    </label>
                </div>
            </section>

            <section class="ei-edit-section">
                <div class="ei-edit-section-head">관제점 연결</div>
                <p class="ei-edit-flow-hint">대상 관제점 값 집계 ÷ 기준 관제점 값 → 결과 저장 관제점 저장</p>

                <div class="ei-edit-source-group">
                    <div class="ei-edit-source-label">대상 관제점</div>
                    <div class="ei-edit-pill-list">
                        <div v-for="(r, idx) in refList" :key="r.ptIdx + '-' + idx" class="ei-edit-ref-pill">
                            <span class="ei-edit-ref-pill-name">{{ r.ptName || r.ptAddr }}</span>
                            <span class="ei-edit-ref-pill-addr">{{ r.ptAddr }}</span>
                            <button type="button" class="ei-edit-ref-pill-remove" @click="refList.splice(idx, 1)">
                                <i class="fas fa-times"></i>
                            </button>
                        </div>
                        <button type="button" class="ei-edit-ref-add-pill" @click="openRefModal">
                            <i class="fas fa-plus"></i> 관제점 추가
                        </button>
                    </div>
                </div>

                <div class="ei-edit-source-group">
                    <div class="ei-edit-source-label">기준 관제점</div>
                    <div v-if="basePointLocked" class="ei-edit-locked-point">
                        <div v-if="basePoint" class="ei-edit-point-chip ei-edit-point-chip--locked">
                            <strong>{{ basePoint.ptName || "—" }}</strong>
                            <span class="ei-edit-point-addr">({{ basePoint.ptAddr }})</span>
                        </div>
                    </div>
                    <div v-else class="ei-edit-source-row">
                        <button type="button" class="ei-edit-btn ei-edit-btn--outline" @click="openBaseModal">
                            <i class="fas fa-search"></i> 관제점 찾기
                        </button>
                        <div v-if="basePoint" class="ei-edit-point-chip">
                            <strong>{{ basePoint.ptName || "—" }}</strong>
                            <span class="ei-edit-point-addr">{{ basePoint.ptAddr }}</span>
                            <span class="ei-edit-point-id">#{{ basePoint.ptIdx }}</span>
                        </div>
                        <span v-else class="ei-edit-empty-text">미연결</span>
                    </div>
                </div>

                <div class="ei-edit-source-group">
                    <div class="ei-edit-source-label">결과 저장 관제점</div>
                    <div v-if="resultPointLocked" class="ei-edit-locked-point">
                        <div v-if="resultPoint" class="ei-edit-point-chip ei-edit-point-chip--locked">
                            <strong>{{ resultPoint.ptName || "—" }}</strong>
                            <span class="ei-edit-point-addr">({{ resultPoint.ptAddr }})</span>
                        </div>
                    </div>
                    <div v-else class="ei-edit-source-row">
                        <span class="ei-edit-empty-text">미연결</span>
                    </div>
                </div>
            </section>

            <div class="ei-edit-footer">
                <button type="button" class="ei-edit-btn ei-edit-btn--ghost" @click="$emit('cancel')">취소</button>
                <button
                    type="button"
                    class="ei-edit-btn ei-edit-btn--primary"
                    :disabled="saving || !canSave"
                    @click="saveAll"
                >
                    <i v-if="saving" class="fas fa-spinner fa-spin"></i> 저장
                </button>
            </div>
        </template>

        <EnIntPointSearchModal
            :visible.sync="showBaseModal"
            :keyword.sync="baseKeyword"
            :point-list-api-state="basePointListApiState"
            title="기준 관제점 (ENINT)"
            @search="filterBase"
            @select="onSelectBase"
        />

        <EnIntPointSearchModal
            :visible.sync="showRefModal"
            :keyword.sync="refKeyword"
            :point-list-api-state="refPointListApiState"
            :hint-pt-groups="['ENSPLY']"
            title="대상 관제점 추가"
            subtitle="중복 추가는 자동 제외"
            @search="filterRef"
            @select="onSelectRef"
        />
    </div>
</template>

<script>
import backEndApi from "@src/api/backEndApi";
import EnIntPointSearchModal from "./EnIntPointSearchModal.vue";
import { ENINT_DATA_INPUT_NEW, ENINT_DATA_INPUT_EXISTING } from "../utils/enIntUi.js";

export default {
    name: "EnIntEditModal",
    components: { EnIntPointSearchModal },
    props: {
        enIntIdx: { type: Number, required: true },
        dateType: { type: String, required: true },
        dateValue: { type: Number, required: true },
    },
    data() {
        return {
            ENINT_DATA_INPUT_NEW,
            ENINT_DATA_INPUT_EXISTING,
            loading: true,
            error: "",
            saving: false,
            master: {},
            edit: { enIntName: "", calcEnabled: "Y", rmk: "", dataInputType: ENINT_DATA_INPUT_NEW },
            basePoint: null,
            refList: [],
            resultPoint: null,
            resultPointLocked: false,
            ptMap: {},
            basePointLocked: false,
            showBaseModal: false,
            baseKeyword: "",
            baseAllPoints: [],
            basePointListApiState: { response: null, data: { rows: [], totalCount: 0 }, loading: false, error: "" },
            showRefModal: false,
            refKeyword: "",
            refAllPoints: [],
            refPointListApiState: { response: null, data: { rows: [], totalCount: 0 }, loading: false, error: "" },
        };
    },
    computed: {
        hasBasePoint() {
            return Boolean(this.basePoint?.ptIdx);
        },
        canSave() {
            return this.hasBasePoint;
        },
    },
    watch: {
        enIntIdx: {
            immediate: true,
            handler() {
                this.bootstrap();
            },
        },
    },
    methods: {
        async bootstrap() {
            this.loading = true;
            this.error = "";
            try {
                const [, getMaster_response] = await Promise.all([
                    this.loadPointMap(),
                    backEndApi.enInt.getMaster({ enIntIdx: this.enIntIdx }),
                ]);
                const getMaster_data = getMaster_response?.data ?? null;
                if (getMaster_response.status !== 200 || !getMaster_data) throw new Error("master");
                this.master = getMaster_data;
                this.edit = {
                    enIntName: this.master.enIntName || "",
                    calcEnabled: this.master.calcEnabled || "Y",
                    rmk: this.master.rmk || "",
                    dataInputType: this.master.dataInputType || ENINT_DATA_INPUT_NEW,
                };
                const srcRows = Array.isArray(this.master.sources) ? this.master.sources : [];
                this.applySources(srcRows);
            } catch {
                this.error = "데이터를 불러오지 못했습니다.";
            } finally {
                this.loading = false;
            }
        },
        async loadPointMap() {
            try {
                const getPointList_map_response = await backEndApi.pointInfo.getPointList();
                const getPointList_map_data = Array.isArray(getPointList_map_response?.data)
                    ? getPointList_map_response.data
                    : [];
                const map = {};
                getPointList_map_data.forEach((p) => {
                    if (p?.ptIdx != null) map[p.ptIdx] = p;
                });
                this.ptMap = map;
            } catch {
                this.ptMap = {};
            }
        },
        applySources(rows) {
            const base = rows.find((r) => r.sourceType === "BASE");
            const result = rows.find((r) => r.sourceType === "RESULT");
            const refs = rows.filter((r) => r.sourceType === "REF");

            const pickPoint = (row) => {
                if (!row?.ptIdx) return null;
                const p = this.ptMap[row.ptIdx];
                return {
                    ptIdx: row.ptIdx,
                    ptAddr: row.ptAddr ?? p?.ptAddr ?? "",
                    ptName: row.ptName ?? p?.ptName ?? "",
                };
            };

            this.basePoint = pickPoint(base);
            this.basePointLocked = Boolean(base?.ptIdx);

            this.refList = refs.map((r) => {
                const p = this.ptMap[r.ptIdx];
                return {
                    ptIdx: r.ptIdx,
                    ptAddr: r.ptAddr ?? p?.ptAddr ?? "",
                    ptName: r.ptName ?? p?.ptName ?? "",
                };
            });

            this.resultPoint = pickPoint(result);
            this.resultPointLocked = Boolean(result?.ptIdx);
        },

        async saveAll() {
            if (!this.basePoint?.ptIdx) {
                this.alertNoti("기준 관제점을 선택하세요.");
                return;
            }
            this.saving = true;
            try {
                const [updateMaster_response, saveSource_response] = await Promise.all([
                    backEndApi.enInt.updateMaster({
                        enIntIdx: this.enIntIdx,
                        enIntName: this.edit.enIntName,
                        rmk: this.edit.rmk || null,
                        calcEnabled: this.edit.calcEnabled,
                        dataInputType: this.edit.dataInputType,
                    }),
                    backEndApi.enInt.saveSource({
                        enIntIdx: this.enIntIdx,
                        basePtIdx: this.basePoint.ptIdx,
                        refList: this.refList.map((r) => ({ ptIdx: r.ptIdx, weight: 1 })),
                    }),
                ]);
                if (updateMaster_response.status !== 200 || saveSource_response.status !== 200) throw new Error("api");
                this.alertNoti("저장되었습니다.");
                this.$emit("saved");
            } catch {
                this.alertNoti("저장에 실패했습니다.");
            } finally {
                this.saving = false;
            }
        },

        async openBaseModal() {
            if (this.basePointLocked) return;
            this.showBaseModal = true;
            this.baseKeyword = "";
            this.basePointListApiState = {
                ...this.basePointListApiState,
                loading: true,
                error: "",
            };
            try {
                const getPointList_base_response = await backEndApi.pointInfo.getPointList();
                const getPointList_base_data = Array.isArray(getPointList_base_response?.data)
                    ? getPointList_base_response.data
                    : [];
                this.baseAllPoints = getPointList_base_data
                    .filter((p) => String(p.enabled || "Y").toUpperCase() === "Y")
                    .map((p) => ({ ptIdx: p.ptIdx, ptAddr: p.ptAddr, ptName: p.ptName, ptGroup: p.ptGroup }));
                this.basePointListApiState = {
                    response: getPointList_base_response,
                    data: { rows: Array.from(this.baseAllPoints), totalCount: this.baseAllPoints.length },
                    loading: false,
                    error: "",
                };
            } catch {
                this.baseAllPoints = [];
                this.basePointListApiState = {
                    response: null,
                    data: { rows: [], totalCount: 0 },
                    loading: false,
                    error: "관제점 목록을 불러오지 못했습니다.",
                };
                this.alertNoti("관제점 목록을 불러오지 못했습니다.");
            }
        },
        filterBase() {
            const kw = this.baseKeyword.trim().toLowerCase();
            if (!kw) {
                this.basePointListApiState = {
                    ...this.basePointListApiState,
                    data: { rows: Array.from(this.baseAllPoints), totalCount: this.baseAllPoints.length },
                    loading: false,
                };
                return;
            }
            const filtered = this.baseAllPoints.filter(
                (p) =>
                    (p.ptAddr && p.ptAddr.toLowerCase().includes(kw)) ||
                    (p.ptName && p.ptName.toLowerCase().includes(kw)),
            );
            this.basePointListApiState = {
                ...this.basePointListApiState,
                data: { rows: filtered, totalCount: this.baseAllPoints.length },
                loading: false,
            };
        },
        onSelectBase(row) {
            this.basePoint = { ptIdx: row.ptIdx, ptAddr: row.ptAddr, ptName: row.ptName };
            this.showBaseModal = false;
        },
        async openRefModal() {
            this.showRefModal = true;
            this.refKeyword = "";
            this.refPointListApiState = {
                ...this.refPointListApiState,
                loading: true,
                error: "",
            };
            try {
                const getPointList_ref_response = await backEndApi.pointInfo.getPointList();
                const getPointList_ref_data = Array.isArray(getPointList_ref_response?.data)
                    ? getPointList_ref_response.data
                    : [];
                this.refAllPoints = getPointList_ref_data
                    .filter((p) => String(p.enabled || "Y").toUpperCase() === "Y")
                    .map((p) => ({ ptIdx: p.ptIdx, ptAddr: p.ptAddr, ptName: p.ptName, ptGroup: p.ptGroup }));
                this.refPointListApiState = {
                    response: getPointList_ref_response,
                    data: { rows: [], totalCount: this.refAllPoints.length },
                    loading: false,
                    error: "",
                };
                this.applyRefFilter();
            } catch {
                this.refAllPoints = [];
                this.refPointListApiState = {
                    response: null,
                    data: { rows: [], totalCount: 0 },
                    loading: false,
                    error: "관제점 목록을 불러오지 못했습니다.",
                };
                this.alertNoti("관제점 목록을 불러오지 못했습니다.");
            }
        },
        applyRefFilter() {
            const existing = new Set(this.refList.map((r) => r.ptIdx));
            const kw = this.refKeyword.trim().toLowerCase();
            let list = this.refAllPoints.filter((p) => !existing.has(p.ptIdx));
            if (kw) {
                list = list.filter(
                    (p) =>
                        (p.ptAddr && p.ptAddr.toLowerCase().includes(kw)) ||
                        (p.ptName && p.ptName.toLowerCase().includes(kw)),
                );
            }
            this.refPointListApiState = {
                ...this.refPointListApiState,
                data: { rows: list, totalCount: this.refAllPoints.length },
                loading: false,
            };
        },
        filterRef() {
            this.applyRefFilter();
        },
        onSelectRef(row) {
            if (this.refList.some((r) => r.ptIdx === row.ptIdx)) return;
            this.refList.push({ ptIdx: row.ptIdx, ptAddr: row.ptAddr, ptName: row.ptName });
            this.showRefModal = false;
        },
    },
};
</script>

<style scoped>
.ei-edit {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.ei-edit-loading {
    padding: 28px;
    text-align: center;
    color: #64748b;
    font-size: 13px;
    font-weight: 600;
}

.ei-edit-error {
    padding: 12px 14px;
    border-radius: 8px;
    background: rgba(220, 38, 38, 0.08);
    color: #991b1b;
    font-size: 13px;
    font-weight: 600;
}

.ei-edit-section {
    border: 1px solid rgba(226, 232, 240, 0.9);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.96);
    padding: 12px 14px;
}

.ei-edit-section-head {
    font-size: 13px;
    font-weight: 700;
    color: #1b263b;
    margin-bottom: 10px;
}

.ei-edit-flow-hint {
    margin: 0 0 10px;
    padding: 7px 10px;
    border-radius: 8px;
    border: 1px solid rgba(14, 116, 144, 0.16);
    background: rgba(14, 116, 144, 0.06);
    font-size: 11px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.45;
}

.ei-edit-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
}

.ei-edit-ro {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.ei-edit-field {
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.ei-edit-field--full {
    grid-column: 1 / -1;
}

.ei-edit-label {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
}

.ei-edit-value {
    font-size: 12px;
    color: #334155;
    font-weight: 600;
}

.ei-edit-input,
.ei-edit-select,
.ei-edit-textarea {
    width: 100%;
    box-sizing: border-box;
    padding: 6px 9px;
    border: 1px solid rgba(203, 213, 225, 0.8);
    border-radius: 6px;
    font-size: 12px;
    color: #1b263b;
    background: #fff;
    outline: none;
    transition: border-color 0.15s;
}

.ei-edit-input:focus,
.ei-edit-select:focus,
.ei-edit-textarea:focus {
    border-color: rgba(14, 116, 144, 0.4);
}

.ei-edit-textarea {
    resize: vertical;
}

/* ── 소스/관제점 ── */
.ei-edit-source-group {
    margin-bottom: 12px;
}

.ei-edit-source-group:last-child {
    margin-bottom: 0;
}

.ei-edit-source-label {
    font-size: 11px;
    font-weight: 700;
    color: #475569;
    margin-bottom: 6px;
}

.ei-edit-source-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

/* ── 잠금 관제점 ── */
.ei-edit-locked-point {
    display: flex;
    align-items: center;
    gap: 8px;
}

.ei-edit-point-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;

    font-size: 12px;
}

.ei-edit-point-chip--locked {
    flex-wrap: wrap;
}

.ei-edit-point-addr {
    color: #64748b;
}

.ei-edit-point-id {
    color: #94a3b8;
    font-size: 10px;
}

.ei-edit-empty-text {
    font-size: 12px;
    color: #94a3b8;
    padding: 4px 0;
}

/* ── 대상 관제점 pill 리스트 ── */
.ei-edit-pill-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
}

.ei-edit-ref-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px 4px 10px;
    border-radius: 999px;
    border: 1px solid rgba(14, 116, 144, 0.2);
    background: rgba(14, 116, 144, 0.04);
    font-size: 11px;
    color: #334155;
}

.ei-edit-ref-pill-name {
    font-weight: 700;
    max-width: 140px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ei-edit-ref-pill-addr {
    color: #64748b;
    font-size: 10px;
}

.ei-edit-ref-pill-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border: none;
    border-radius: 50%;
    background: rgba(148, 163, 184, 0.18);
    color: #64748b;
    font-size: 8px;
    cursor: pointer;
    transition:
        background 0.15s,
        color 0.15s;
    padding: 0;
    flex-shrink: 0;
}

.ei-edit-ref-pill-remove:hover {
    background: rgba(239, 68, 68, 0.15);
    color: #dc2626;
}

.ei-edit-ref-add-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 999px;
    border: 1px dashed rgba(14, 116, 144, 0.3);
    background: transparent;
    color: #0e7490;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition:
        background 0.15s,
        border-color 0.15s;
}

.ei-edit-ref-add-pill:hover {
    background: rgba(14, 116, 144, 0.06);
    border-color: rgba(14, 116, 144, 0.45);
}

/* ── 버튼 ── */
.ei-edit-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 6px 12px;
    border-radius: 6px;
    border: 1px solid transparent;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition:
        background 0.15s,
        border-color 0.15s;
    white-space: nowrap;
}

.ei-edit-btn--primary {
    background: #0e7490;
    color: #fff;
    border-color: #0e7490;
}

.ei-edit-btn--primary:hover {
    background: #0c6a82;
}

.ei-edit-btn--primary:disabled {
    opacity: 0.5;
    cursor: default;
}

.ei-edit-btn--outline {
    background: #fff;
    color: #0e7490;
    border-color: rgba(14, 116, 144, 0.3);
}

.ei-edit-btn--outline:hover {
    background: rgba(14, 116, 144, 0.04);
}

.ei-edit-btn--ghost {
    background: transparent;
    color: #475569;
    border-color: rgba(226, 232, 240, 0.9);
}

.ei-edit-btn--ghost:hover {
    background: rgba(241, 245, 249, 0.9);
}

/* ── 푸터 ── */
.ei-edit-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 8px;
    padding-top: 4px;
}

.ei-edit-footer-hint {
    margin-right: auto;
    font-size: 11px;
    color: #64748b;
}

@media (max-width: 720px) {
    .ei-edit-footer {
        flex-direction: column;
        align-items: stretch;
    }

    .ei-edit-footer-hint {
        margin-right: 0;
    }

    .ei-edit-btn {
        justify-content: center;
    }
}
</style>
